/**
 * Stripe Service
 *
 * Handles Stripe-related operations via Cloud Functions
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db, app } from "../config/firebase";
import { PlansConfig, PlansConfigV3, PlanTier, PlanTierV3, Currency, SubscriptionDetails, ViewLicense, UserCreditsPool } from "../types";

// Initialize functions with region
const functions = getFunctions(app, "europe-west1");

// Cloud Function callables
const createCheckoutSessionFn = httpsCallable<
  {
    viewId: string;
    projectId: string;
    planTier: PlanTier;
    currency: Currency;
    successUrl: string;
    cancelUrl: string;
  },
  { sessionId: string; url: string }
>(functions, "createCheckoutSession");

const createCustomerPortalSessionFn = httpsCallable<
  { returnUrl: string },
  { url: string }
>(functions, "createCustomerPortalSession");

const getSubscriptionDetailsFn = httpsCallable<
  { viewId: string; projectId: string },
  SubscriptionDetails
>(functions, "getSubscriptionDetails");

// User-level subscription cancellation
const cancelSubscriptionFn = httpsCallable<
  Record<string, never>,
  {
    success: boolean;
    effectiveAt: string;
    willExceedLimits: boolean;
    excessViews: number;
    excessStorageBytes: number;
  }
>(functions, "cancelSubscription");

const resumeSubscriptionFn = httpsCallable<
  Record<string, never>,
  { success: boolean }
>(functions, "resumeSubscription");

// Change plan between Start and Pro
const changePlanFn = httpsCallable<
  { targetTier: "start" | "pro" },
  {
    success: boolean;
    isDowngrade: boolean;
    effectiveAt: string;
    willExceedLimits: boolean;
    excessViews: number;
    excessStorageBytes: number;
  }
>(functions, "changePlan");

// Cancel pending plan change (undo scheduled downgrade)
const cancelPlanChangeFn = httpsCallable<
  Record<string, never>,
  { success: boolean }
>(functions, "cancelPlanChange");

// User-level checkout (new pricing model)
// Uses the same createCheckoutSession function (already user-level)
const createUserCheckoutSessionFn = httpsCallable<
  {
    planTier: PlanTier;
    currency: Currency;
    successUrl: string;
    cancelUrl: string;
  },
  { sessionId: string; url: string }
>(functions, "createCheckoutSession");

/**
 * Create a Stripe Checkout session for upgrading a view
 * @deprecated Use createUserCheckoutSession for user-level subscriptions
 */
export async function createCheckoutSession(params: {
  viewId: string;
  projectId: string;
  planTier: PlanTier;
  currency: Currency;
}): Promise<string> {
  const { viewId, projectId, planTier, currency } = params;

  // Build success/cancel URLs
  const baseUrl = window.location.origin;
  const successUrl = `${baseUrl}/projects/${projectId}/views/${viewId}?upgrade=success`;
  const cancelUrl = `${baseUrl}/projects/${projectId}/views/${viewId}?upgrade=cancelled`;

  const result = await createCheckoutSessionFn({
    viewId,
    projectId,
    planTier,
    currency,
    successUrl,
    cancelUrl,
  });

  return result.data.url;
}

/**
 * Create a Stripe Checkout session for user-level subscription upgrade
 * This is the new pricing model (per-user, not per-view)
 */
export async function createUserCheckoutSession(params: {
  planTier: PlanTier;
  currency: Currency;
}): Promise<string> {
  const { planTier, currency } = params;

  // Build success/cancel URLs
  const baseUrl = window.location.origin;
  const successUrl = `${baseUrl}/account?upgrade=success`;
  const cancelUrl = `${baseUrl}/account?upgrade=cancelled`;

  const result = await createUserCheckoutSessionFn({
    planTier,
    currency,
    successUrl,
    cancelUrl,
  });

  return result.data.url;
}

/**
 * Create a Stripe Customer Portal session for managing subscriptions
 */
export async function createCustomerPortalSession(): Promise<string> {
  const returnUrl = window.location.href;

  const result = await createCustomerPortalSessionFn({ returnUrl });

  return result.data.url;
}

/**
 * Get subscription details for a view
 */
export async function getSubscriptionDetails(params: {
  viewId: string;
  projectId: string;
}): Promise<SubscriptionDetails> {
  const result = await getSubscriptionDetailsFn(params);
  return result.data;
}

/**
 * Cancel subscription (downgrade to Free at end of billing period)
 * Returns info about whether user exceeds Free plan limits
 */
export async function cancelSubscription(): Promise<{
  success: boolean;
  effectiveAt: string;
  willExceedLimits: boolean;
  excessViews: number;
  excessStorageBytes: number;
}> {
  const result = await cancelSubscriptionFn({});
  return result.data;
}

/**
 * Resume subscription (undo cancel)
 */
export async function resumeSubscription(): Promise<{ success: boolean }> {
  const result = await resumeSubscriptionFn({});
  return result.data;
}

/**
 * Change plan between Start and Pro
 * For downgrade: takes effect at end of billing period
 * For upgrade: takes effect immediately with prorated billing
 */
export async function changePlan(targetTier: "start" | "pro"): Promise<{
  success: boolean;
  isDowngrade: boolean;
  effectiveAt: string;
  willExceedLimits: boolean;
  excessViews: number;
  excessStorageBytes: number;
}> {
  const result = await changePlanFn({ targetTier });
  return result.data;
}

/**
 * Cancel pending plan change (undo scheduled downgrade)
 */
export async function cancelPlanChange(): Promise<{ success: boolean }> {
  const result = await cancelPlanChangeFn({});
  return result.data;
}

/**
 * Get plans configuration from Firestore
 */
export async function getPlansConfig(): Promise<PlansConfig | null> {
  const docRef = doc(db, "config", "plans");
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return docSnap.data() as PlansConfig;
}

/**
 * Subscribe to plans configuration changes
 */
export function subscribePlansConfig(
  callback: (config: PlansConfig | null) => void
): () => void {
  const docRef = doc(db, "config", "plans");

  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data() as PlansConfig);
    } else {
      callback(null);
    }
  });
}

// === V3.1 Per-View Functions (1 subscription per view) ===

const createCheckoutSessionV3Fn = httpsCallable<
  { tier: PlanTierV3; viewId: string; projectId: string; successUrl: string; cancelUrl: string },
  { sessionId?: string; url?: string }
>(functions, "createCheckoutSessionV3");

const upgradeViewV3Fn = httpsCallable<
  { viewId: string; targetTier: "hobby" | "plus" | "pro" },
  { success: boolean; newTier: string; bonusCreditsGranted: number }
>(functions, "upgradeViewV3");

const downgradeViewV3Fn = httpsCallable<
  { viewId: string; targetTier: "hobby" | "plus" | "pro" },
  { success: boolean; targetTier: string; effectiveAt: string | null }
>(functions, "downgradeViewV3");

const cancelViewV3Fn = httpsCallable<
  { viewId: string },
  { success: boolean; effectiveAt: string | null }
>(functions, "cancelViewV3");

const resumeViewV3Fn = httpsCallable<
  { viewId: string },
  { success: boolean }
>(functions, "resumeViewV3");

const getAccountDetailsV3Fn = httpsCallable<
  Record<string, never>,
  {
    credits: UserCreditsPool;
    autoPayOverage: boolean;
    overageCreditsAccumulated: number;
    stripeCustomerId: string | null;
    viewLicenses: (ViewLicense & { viewId: string })[];
  }
>(functions, "getAccountDetailsV3");

const toggleAutoPayOverageV3Fn = httpsCallable<
  { enabled: boolean },
  { success: boolean; autoPayOverage: boolean }
>(functions, "toggleAutoPayOverageV3");

export async function createCheckoutSessionV3(params: {
  tier: PlanTierV3;
  viewId: string;
  projectId: string;
}): Promise<string> {
  const { tier, viewId, projectId } = params;
  const baseUrl = window.location.origin;

  const result = await createCheckoutSessionV3Fn({
    tier,
    viewId,
    projectId,
    successUrl: `${baseUrl}/account?upgrade=success`,
    cancelUrl: `${baseUrl}/account?upgrade=cancelled`,
  });

  return result.data.url!;
}

export async function upgradeViewV3(viewId: string, targetTier: "hobby" | "plus" | "pro") {
  const result = await upgradeViewV3Fn({ viewId, targetTier });
  return result.data;
}

export async function downgradeViewV3(viewId: string, targetTier: "hobby" | "plus" | "pro") {
  const result = await downgradeViewV3Fn({ viewId, targetTier });
  return result.data;
}

export async function cancelViewV3(viewId: string) {
  const result = await cancelViewV3Fn({ viewId });
  return result.data;
}

export async function resumeViewV3(viewId: string) {
  const result = await resumeViewV3Fn({ viewId });
  return result.data;
}

export async function getAccountDetailsV3() {
  const result = await getAccountDetailsV3Fn({});
  return result.data;
}

export async function toggleAutoPayOverageV3(enabled: boolean) {
  const result = await toggleAutoPayOverageV3Fn({ enabled });
  return result.data;
}

export async function getPlansConfigV3(): Promise<PlansConfigV3 | null> {
  const docRef = doc(db, "config", "plans_v3");
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? (docSnap.data() as PlansConfigV3) : null;
}

// === V3.1 Add-on Functions ===

const purchaseStorageAddonV3Fn = httpsCallable<
  { viewId: string; quantity: number; successUrl: string; cancelUrl: string },
  { sessionId: string; url: string }
>(functions, "purchaseStorageAddonV3");

const cancelStorageAddonV3Fn = httpsCallable<
  { viewId: string },
  { success: boolean }
>(functions, "cancelStorageAddonV3");

const purchaseCreditsAddonV3Fn = httpsCallable<
  { packs: number; successUrl: string; cancelUrl: string },
  { sessionId: string; url: string }
>(functions, "purchaseCreditsAddonV3");

export async function purchaseStorageAddonV3(viewId: string, quantity: number): Promise<string> {
  const baseUrl = window.location.origin;
  const result = await purchaseStorageAddonV3Fn({
    viewId,
    quantity,
    successUrl: `${baseUrl}/account?addon=storage-success`,
    cancelUrl: `${baseUrl}/account?addon=storage-cancelled`,
  });
  return result.data.url;
}

export async function cancelStorageAddonV3(viewId: string): Promise<{ success: boolean }> {
  const result = await cancelStorageAddonV3Fn({ viewId });
  return result.data;
}

export async function purchaseCreditsAddonV3(packs: number): Promise<string> {
  const baseUrl = window.location.origin;
  const result = await purchaseCreditsAddonV3Fn({
    packs,
    successUrl: `${baseUrl}/account?addon=credits-success`,
    cancelUrl: `${baseUrl}/account?addon=credits-cancelled`,
  });
  return result.data.url;
}

export function subscribePlansConfigV3(
  callback: (config: PlansConfigV3 | null) => void
): () => void {
  const docRef = doc(db, "config", "plans_v3");
  return onSnapshot(docRef, (docSnap) => {
    callback(docSnap.exists() ? (docSnap.data() as PlansConfigV3) : null);
  });
}

/**
 * Detect user's preferred currency based on locale/timezone
 */
export function detectCurrency(): Currency {
  // Check browser locale
  const locale = navigator.language || "en-US";

  // European locales typically use EUR
  const eurLocales = [
    "de",
    "fr",
    "it",
    "es",
    "pt",
    "nl",
    "be",
    "at",
    "ie",
    "fi",
    "gr",
    "sk",
    "si",
    "ee",
    "lv",
    "lt",
    "mt",
    "cy",
    "lu",
  ];

  const langCode = locale.split("-")[0].toLowerCase();

  if (eurLocales.includes(langCode)) {
    return "eur";
  }

  // Check timezone for Europe
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone.startsWith("Europe/")) {
    return "eur";
  }

  // Default to USD
  return "usd";
}

/**
 * Format price for display
 */
export function formatPrice(cents: number, currency: Currency): string {
  const amount = cents / 100;
  const currencyCode = currency.toUpperCase();

  return new Intl.NumberFormat(currency === "eur" ? "de-DE" : "en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(amount);
}

/**
 * Format storage for display
 */
export function formatStorage(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);

  if (gb >= 1000) return `${(gb / 1000).toFixed(1)} TB`;
  if (gb >= 10) return `${Math.round(gb)} GB`;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;

  const mb = bytes / (1024 * 1024);
  return `${Math.round(mb)} MB`;
}
